import { createSelector } from "reselect"
import { get, chain, pickBy, map, find, last, isEmpty } from "lodash"
import moment from "moment"
import { CATEGORIES } from "helpers/analyzeConstants"
import { getDisplayType, getDisabled, getFactors } from "./items"
import { getBeta } from "./beta"
import { getConcentrationData, hasReferenceEquity } from "./concentration"
import { getStartDate, getEndDate } from "selectors/dates"
import { computeCategoryItemsSubset, computeDisplayCategories, computeNonZerosKeys } from "helpers/analyze"
import { combineResourcesStatuses, isLoading } from "helpers/data-statuses"
import { makeComputeDriftFromData } from "helpers/drift"
import { getAllResourcesStatuses } from "selectors/data"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  return factor.overviewLabel || factor.label
}

export function computeData(items, data) {
  return chain(data)
    .compact()
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          const isDynamicBeta = item.id === "custom" && item.category === "beta"
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, !isDynamicBeta ? 0 : undefined),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getSelectedRawData = createSelector(
  [getDisplayType, getBeta, getConcentrationData, hasReferenceEquity],
  (displayType, betaData, concentrationData) => displayType === "beta" ? betaData : concentrationData,
)

export const getCurrent = createSelector(
  [getSelectedRawData],
  (rawData) => last(rawData),
)

export const getAllItems = createSelector(
  [getCurrent, getDisabled, getFactors],
  (lastDatum, disabled, factors) => map(factors, (values, id) => ({
    id,
    label: getFactorLabel(factors, id),
    category: get(values, "category"),
    lastValue: get(lastDatum, ["factors", id], 0),
    field: `factors.${id}`,
    disabled: !!disabled[id],
    area: get(values, "area"),
    percentage: get(values, "percentage"),
    tooltip: get(values, "tooltip"),
    color: get(values, "color"),
  })),
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getDisplayType],
  (items, displayType) => chain(items)
    .filter(item => item.category === displayType)
    .orderBy(item => Math.abs(item.lastValue), ["desc"])
    .value(),
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getDisplayType],
  (items, displayType) => {
    return chain(items)
      .filter(item => item.category === displayType)
      .orderBy(item => Math.abs(item.lastValue), ["asc", "desc"])
      .value()
  },
)

export const getFilteredRawData = createSelector(
  [getSelectedRawData, getStartDate, getEndDate],
  (selectedData, startDate, endDate) =>
    selectedData.filter((data) => {
      if (data && data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getData = createSelector(
  [getAllItems, getFilteredRawData],
  (items, filteredRawData) => computeData(items, filteredRawData),
)

const getNonZerosKeys = createSelector(
  [getData],
  computeNonZerosKeys,
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys, getCategoryItemsSubset],
  (data, keys, items) => isEmpty(items) ? EMPTY_ARRAY : map(data, item => pickBy(item, (item, key) => keys[key])),
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  computeDisplayCategories,
)

export const getDrift = createSelector(
  [getBeta],
  makeComputeDriftFromData(item => get(item, "factors", EMPTY_OBJECT)),
)

export const getSelectedOption = createSelector(
  [getDisplayType],
  (displayType) => {
    return find(CATEGORIES, { id: displayType })
  },
)

export const getPassthroughResourcesStatus = createSelector(
  [getAllResourcesStatuses],
  (statuses) => combineResourcesStatuses(["beta", "dynamicBeta", "concentration"], statuses),
)

export const getIsLoading = createSelector(
  [getPassthroughResourcesStatus],
  isLoading,
)
